import {
  getTotalCostIfDefined,
  getUpdatedScanValue,
  ScanLineItemOptimized,
  ScanOptionOptimized,
  ScanTemplateOption,
} from '@kaiizen/kai-scan-template-system';
import React, { useCallback } from 'react';

import { formatUsd } from '@/Client/Presenters';
import { ScanDataMutators, useScanEditingContextSelector, useScanPartNotes } from '@/Client/State';

import { FocusedInlineLineItemEditor } from './FocusedInlineLineItemEditor';
import { FocusedScanPartNotesButton } from './FocusedScanPartNotesButton';
import {
  FOCUSED_OPT_CARD,
  FOCUSED_OPT_CARD_DESCRIPTION,
  FOCUSED_OPT_CARD_LINE_ITEMS,
  FOCUSED_OPT_CARD_MENU,
  FOCUSED_OPT_CARD_NAME,
  FOCUSED_OPT_CARD_NOTES_BUTTON,
  FOCUSED_OPT_CARD_TOP,
  FOCUSED_OPT_CARD_TOTAL,
} from './focusedTaskCardStyles';

/**
 * A preview line item gets a new id each time the task's previews are rebuilt (when the task or its products change), so
 * its row is keyed by its template and stays mounted, the way a line item in the scan stays keyed by its id.
 */
const getLineItemRowKey = (lineItemTemplateId: string, index: number, realLineItem: ScanLineItemOptimized | undefined) =>
  realLineItem ? realLineItem.id : `${lineItemTemplateId}-${index}`;

export const FocusedInlineOptionEditor = React.memo(({
  preview,
  optionTemplate,
  option,
  taskId,
  groupId,
  scanDataMutators,
}: {
  /** The option is not in the scan data; `option` is its vivified preview. */
  preview?: boolean;
  optionTemplate: ScanTemplateOption;
  option?: ScanOptionOptimized;
  taskId: string;
  groupId: string;
  scanDataMutators: ScanDataMutators;
}) => {
  const { scanPartNotes: notes } = useScanPartNotes(
    option?.id,
  );

  const handleCreateOption = useCallback(() => {
    const firstLineItemTemplate = optionTemplate.lineItemTemplates?.[0];
    if (!firstLineItemTemplate) return;
    const [firstValueKey, firstValueTemplate] = Object.entries(firstLineItemTemplate.values ?? {})[0] ?? [];
    if (!firstValueKey || !firstValueTemplate) return;

    scanDataMutators.addOption({
      groupId,
      taskId,
      optionTemplateId: optionTemplate.id,
      addLineItemTemplateId: firstLineItemTemplate.id,
      addLineItemInitializers: {
        values: { [firstValueKey]: getUpdatedScanValue(firstValueTemplate, undefined, 0) },
      },
    });
  }, [groupId, taskId, optionTemplate, scanDataMutators]);

  // the option as it is in the scan data: anything else in `option` is a vivified preview
  const realOption = useScanEditingContextSelector(
    ({ scanRoot }) => preview || !option ? undefined : scanRoot?.groupsById[groupId]?.tasksById[taskId]?.optionsById[option.id],
    [preview, option?.id, groupId, taskId],
  );
  // what's in the scope: the real option's cost, never its previews'
  const optionCost = getTotalCostIfDefined(realOption);

  return (
    <div css={FOCUSED_OPT_CARD}>
      <div css={FOCUSED_OPT_CARD_TOP}>
        <div css={FOCUSED_OPT_CARD_NAME}>{optionTemplate.name}</div>
        <div css={FOCUSED_OPT_CARD_TOTAL}>{formatUsd(optionCost, false, true)}</div>
        <div css={FOCUSED_OPT_CARD_MENU}>
          <div css={FOCUSED_OPT_CARD_NOTES_BUTTON}>
            <FocusedScanPartNotesButton
              scanPart={option}
              onCreateScanPart={handleCreateOption}
              notes={notes}
            />
          </div>
        </div>
      </div>
      {optionTemplate.description && <div css={FOCUSED_OPT_CARD_DESCRIPTION}>{optionTemplate.description}</div>}
      <div css={FOCUSED_OPT_CARD_LINE_ITEMS}>
        {optionTemplate?.lineItemTemplates?.map(lineItemTemplate => {
          const filtered = option?.lineItems?.filter(lineItem => lineItem.templateId === lineItemTemplate.id);
          const lineItems: (ScanLineItemOptimized | undefined)[] = filtered?.length ? filtered : [undefined];
          return lineItems.map((lineItem, index) => {
            // a line item is in the scope exactly when it exists in the scan; any other row is its template's preview
            const realLineItem = lineItem && realOption?.lineItems?.find(({ id }) => id === lineItem.id);
            return (
              <FocusedInlineLineItemEditor
                key={getLineItemRowKey(lineItemTemplate.id, index, realLineItem)}
                lineItemTemplate={lineItemTemplate}
                optionTemplate={optionTemplate}
                lineItem={lineItem}
                taskId={taskId}
                groupId={groupId}
                optionId={option?.id}
                scanDataMutators={scanDataMutators}
                preview={!realLineItem}
                optionExists={!!realOption}
              />
            );
          });
        })}
      </div>
    </div>
  );
});
